import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useSnackbar } from 'notistack';
import { useDeleteVocabularyEntry, useSetVocabularyEntry } from '@/api/queries';

export interface VocabularyColumn {
  /** Key on the entry, and on the form state. */
  field: string;
  label: string;
  /** Blocks save while empty. `id` and `name` are required everywhere. */
  required?: boolean;
  type?: 'text' | 'number' | 'boolean';
  helper?: string;
}

export interface VocabularyPageProps {
  title: string;
  description: string;
  /** GraphQL query name, e.g. `genres` — also the react-query cache key. */
  queryName: string;
  /** Mutation names, e.g. `setGenre` / `deleteGenre`. */
  setMutation: string;
  deleteMutation: string;
  columns: VocabularyColumn[];
  entries?: Record<string, unknown>[];
  isLoading: boolean;
  error?: unknown;
  /** Identifying key. Every vocabulary uses `id` except the category tree, keyed by `path`. */
  idField?: string;
  /** Category paths are derived from the items themselves and have no CRUD mutations. */
  readOnly?: boolean;
  readOnlyReason?: string;
}

/**
 * One screen for every controlled vocabulary.
 *
 * These are the same screen with different columns — six near-identical pages is where the seventh
 * gets forgotten and the third drifts. The config table already exposes generic CRUD, so the UI
 * matches that shape rather than inventing a bespoke page per entity.
 *
 * Editing is admin-only at the API (`@aws_auth(cognito_groups: ["admin"])`). The UI does not hide the
 * buttons for other roles: a non-admin gets a clear authorization error from the server, which is
 * more honest than a screen that silently offers less than the API does — and the server is the only
 * place the rule can actually be enforced.
 */
export default function Vocabulary({
  title,
  description,
  queryName,
  setMutation,
  deleteMutation,
  columns,
  entries,
  isLoading,
  error,
  idField = 'id',
  readOnly,
  readOnlyReason,
}: VocabularyPageProps) {
  const { enqueueSnackbar } = useSnackbar();
  const save = useSetVocabularyEntry(setMutation, queryName);
  const remove = useDeleteVocabularyEntry(deleteMutation, queryName);

  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  /** True when the dialog is creating rather than editing, so the id stays editable. */
  const [isNew, setIsNew] = useState(false);

  const openNew = () => {
    setEditing(Object.fromEntries(columns.map((c) => [c.field, ''])));
    setIsNew(true);
  };

  const commit = async () => {
    if (!editing) return;
    try {
      const input = Object.fromEntries(
        columns.map((c) => {
          const raw = editing[c.field];
          if (c.type === 'number') {
            return [c.field, raw === '' || raw == null ? null : Number(raw)];
          }
          if (c.type === 'boolean') {
            return [c.field, raw === true || raw === 'true'];
          }
          return [c.field, raw ?? ''];
        }),
      );
      await save.mutateAsync(input);
      enqueueSnackbar('Saved', { variant: 'success' });
      setEditing(null);
    } catch (e: unknown) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Save failed', {
        variant: 'error',
      });
    }
  };

  const confirmDelete = async (entry: Record<string, unknown>) => {
    try {
      await remove.mutateAsync(String(entry[idField]));
      enqueueSnackbar('Deleted', { variant: 'success' });
    } catch (e: unknown) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Delete failed', {
        variant: 'error',
      });
    }
  };

  const missingRequired = columns
    .filter((c) => c.required)
    .some((c) => !String(editing?.[c.field] ?? '').trim());

  return (
    <>
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h5">{title}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {!readOnly && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Add
          </Button>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>

      {readOnly && readOnlyReason && (
        <Alert severity="info">{readOnlyReason}</Alert>
      )}

      {error != null && (
        <Alert severity="error">
          {error instanceof Error ? error.message : `Failed to load ${title}`}
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c.field}>{c.label}</TableCell>
                ))}
                {!readOnly && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {(entries ?? []).map((entry) => (
                <TableRow key={String(entry[idField])} hover>
                  {columns.map((c) => (
                    <TableCell key={c.field}>
                      {c.type === 'boolean'
                        ? entry[c.field]
                          ? 'Yes'
                          : 'No'
                        : (entry[c.field] as string) ?? '—'}
                    </TableCell>
                  ))}
                  {!readOnly && (
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditing({ ...entry });
                            setIsNew(false);
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => void confirmDelete(entry)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!(entries ?? []).length && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <Typography variant="body2" color="text.secondary">
                      Nothing configured yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{isNew ? `New ${title}` : `Edit ${title}`}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            {columns.map((c) => (
              <TextField
                key={c.field}
                label={c.label}
                required={c.required}
                type={c.type === 'number' ? 'number' : 'text'}
                // The id is the primary key: changing it on an existing entry would create a second
                // row rather than rename the first, because the write is an upsert.
                disabled={c.field === idField && !isNew}
                helperText={
                  c.field === idField && !isNew
                    ? 'The id cannot be changed — delete and recreate instead.'
                    : c.helper
                }
                value={String(editing?.[c.field] ?? '')}
                onChange={(e) =>
                  setEditing((prev) => ({ ...prev, [c.field]: e.target.value }))
                }
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={missingRequired || save.isPending}
            onClick={() => void commit()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
